import type { ConversationListEntry } from '../../types';
import { IconLock, IconShield } from '../Icon';

interface InfoEncryptionProps {
  conversation: ConversationListEntry;
}

export function InfoEncryption({ conversation }: InfoEncryptionProps) {
  return (
    <div className="px-5 py-6 space-y-5 max-w-md">
      <div className="flex flex-col items-center text-center pt-2">
        <div className="size-14 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 flex items-center justify-center mb-3">
          <IconLock size={22} className="text-emerald-400" />
        </div>
        <h3 className="text-base font-semibold">Messages stay private</h3>
        <p className="text-xs text-zinc-400 mt-2 max-w-xs">
          Your conversation is encrypted end-to-server. Each conversation has its own
          data-encryption key, wrapped under a workspace key that only PGOC controls —
          no third party (including the underlying cloud storage provider) can read your
          messages or attachments at rest.
        </p>
      </div>

      <ul className="space-y-2 text-xs text-zinc-300">
        <Bullet>
          <strong>Per-conversation keys.</strong> Even a stolen disk image can't read
          this chat — each room has its own DEK, wrapped under a workspace KEK derived
          from a master key held only by PGOC infrastructure.
        </Bullet>
        <Bullet>
          <strong>XChaCha20-Poly1305 AEAD.</strong> Industry-standard authenticated
          encryption with a 192-bit nonce — zero risk of nonce reuse across the
          lifetime of a key.
        </Bullet>
        <Bullet>
          <strong>Attachments wrapped twice.</strong> Each file is encrypted with a
          unique per-file key, which is itself wrapped under the conversation DEK.
          Cloud storage only ever sees ciphertext.
        </Bullet>
        <Bullet>
          <strong>Server-mediated, not client-to-client.</strong> The server is in the
          decryption path so it can run search, audit, and compliance checks on PGOC
          data — but the encrypted data never leaves PGOC infrastructure.
        </Bullet>
      </ul>

      <div className="rounded-md border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-[11px] text-zinc-400 flex gap-3">
        <span className="shrink-0 text-amber-400 mt-0.5">
          <IconShield size={14} />
        </span>
        <p>
          This is workspace-managed encryption — not user-to-user end-to-end. PGOC
          administrators with appropriate access can recover history for compliance,
          legal hold, or account-recovery purposes. {conversation.type === 'dm' ? 'DMs follow the same model as group chats.' : 'Group chats follow the same model as DMs.'}
        </p>
      </div>

      <div className="rounded-md border border-zinc-800 bg-zinc-900/30 px-4 py-3 text-[11px] text-zinc-400 leading-relaxed">
        <p className="font-medium text-zinc-300 mb-1">Verify security code</p>
        <p>
          WhatsApp's verify-code flow compares per-device public keys. Our model
          doesn't expose those: keys are workspace-managed, not user-pinned, so
          there's nothing meaningful for two members to compare in person. If
          you suspect a workspace-key compromise, contact PGOC IT — they can
          rotate the workspace KEK, which re-wraps every conversation DEK.
        </p>
      </div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="shrink-0 text-emerald-400 leading-5">·</span>
      <span>{children}</span>
    </li>
  );
}
